import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import Image from "common/blocks/Image";
import RichText from "common/blocks/RichText";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import { Sidebar } from "common/providers/modal/useSidebar";
import { ResolvedHigherOfferSidePanel } from "resolvers/data/higherOfferSidePanelResolver";
import { PartExchange } from "services/motorpoint/partExchange";
import formatCurrency from "utils/formatCurrency";
import parseLegacyDate from "utils/parseLegacyDate";

import Wrapper from "../common/Wrapper";
import styles from "./ValuationRange.module.scss";

const cx = classNames.bind(styles);

export type ValuationRangeModalProps = {
    higherOfferSidePanel?: ResolvedHigherOfferSidePanel;
    partExchange: PartExchange;
    previousSidebar?: Sidebar;
    closingModalGoesToPrevious?: boolean;
};

const ValuationRange = (props: ValuationRangeModalProps) => {
    const { useSidebar } = useModalContext();
    const { closeSidebar } = useSidebar();

    const validUntilDate: Date = parseLegacyDate(props.partExchange.ExpiryDate);

    const now = Date.now();
    const difference = validUntilDate.getTime() - now;
    const totalDays = Math.floor(difference / (1000 * 3600 * 24));

    const yyyy = validUntilDate.getFullYear().toString();
    let mm: string | number = validUntilDate.getMonth() + 1;
    let dd: string | number = validUntilDate?.getDate();
    if (dd && dd < 10) dd = "0" + dd.toString();
    if (mm && mm < 10) mm = "0" + mm.toString();

    const vehicleInfo = `${props.partExchange.Make} ${props.partExchange.Model} ${props.partExchange.Trim} ${props.partExchange.Colour}`;

    return (
        <Wrapper>
            <div className={cx("vehicleConditionWrapper")}>
                <div className={cx("vehicleInfoWrapper")}>
                    <div className={cx("image")}>
                        <Image
                            src={props.partExchange.ValuationImageUri ?? ""}
                            alt={vehicleInfo}
                            width={133.3}
                            height={100}
                        />
                    </div>
                    <span className={cx("vehicleInfo")}>{vehicleInfo}</span>
                    <div className={cx("valuationInfo")}>
                        <div className={cx("valuationInfoWrapper")}>
                            <span className={cx("header")}>Initial valuation</span>
                            <span
                                className={cx("valuation")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(props.partExchange.Valuation, 2, true),
                                }}
                            />
                            <div className={cx("orangeColour")} />
                        </div>
                        <div className={cx("valuationInfoWrapper")}>
                            <span className={cx("header")}>High valuation</span>
                            <span
                                className={cx("valuation")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(
                                        props.partExchange.ValuationRange.MaximumValuation,
                                        2,
                                        true
                                    ),
                                }}
                            />
                            <div className={cx("greenColour")} />
                        </div>
                    </div>
                    {props?.higherOfferSidePanel?.higherOfferModalCopy && (
                        <RichText
                            className={cx("copy")}
                            {...props.higherOfferSidePanel.higherOfferModalCopy}
                        />
                    )}
                    <div className={cx("grayDivider")} />
                    <div className={cx("phoneDetailsWrapper")}>
                        <div className={cx("infoIcon")}>
                            <UIIcon icon="Info" />
                        </div>
                        <span className={cx("phoneDetails")}>
                            {props?.higherOfferSidePanel?.higherOfferModalPhoneDetails}
                        </span>
                        <div className={cx("phoneWrapper")}>
                            <div className={cx("phoneIcon")}>
                                <UIIcon icon="Tel" />
                            </div>
                            <DynamicPhoneNumber isLink={true} className={cx("phoneNumber")} />
                        </div>
                    </div>
                </div>
                <div className={cx("valuationValidity")}>
                    <div className={cx("icon")}>
                        <UIIcon icon="CurrencyExchange" />
                    </div>
                    <div className={cx("validityWrapper")}>
                        <p className={cx("valuationExpiry")}>
                            Your valuation is valid for {totalDays} day{totalDays > 1 ? "s" : ""}{" "}
                            until {`${dd}/${mm}/${yyyy}`}.
                        </p>
                        <p className={cx("validityText")}>
                            {props?.higherOfferSidePanel?.higherOfferModalValidityText}
                        </p>
                    </div>
                </div>
                <Button
                    theme="primary"
                    type="button"
                    fullWidth
                    onClick={() => closeSidebar(true)}
                    ariaLabel="Done"
                >
                    Done
                </Button>
            </div>
        </Wrapper>
    );
};

export default ValuationRange;
